import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Sidebar from "../../../../components/Layout/Sidebar";
import Header from "../../../../components/Layout/Header";
import Footer from "../../../../components/Layout/Footer";
import FormSkeleton from "../../../components/skeleton/FormSkeleton";

import { Form } from "react-bootstrap";
import Swal from "sweetalert2";
import Select from "react-select";

import {
  getData,
  addData,
  updateData,
  getcountries,
  getParticipantTypeData,
  getPaymentReceiptData,
  getFreeParticipantData,
  getOnsiteParticipantData
} from "../../../../services/event/participants/participantSetupService";

import { nanoid } from "nanoid";

/* ── Styles ─────────────────────────────────────────── */
const S = {
  page: {
    background: "#f4f6f9",
    minHeight: "100vh",
    fontFamily: "'DM Sans', sans-serif",
  },
  container: { padding: "28px 28px 0" },
  topBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 10,
  },
  breadcrumb: {
    fontSize: 12,
    color: "#9aa5b4",
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a2332",
    margin: 0,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 18px",
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    background: "#fff",
    color: "#4a5568",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
  },
  /* section card — overflow removed so react-select menus are not clipped */
  sectionCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e3e8ef",
    marginBottom: 20,
    /* overflow: "hidden" intentionally removed — was clipping Select dropdowns */
    boxShadow: "0 1px 8px rgba(26,107,60,0.05)",
  },
  sectionHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid #f0f4f8",
    display: "flex",
    alignItems: "center",
    gap: 10,
    /* keep rounded top corners explicitly since overflow:hidden is gone */
    borderRadius: "14px 14px 0 0",
  },
  sectionIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: "#e8f5ee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: "#1a6b3c",
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1a2332",
    margin: 0,
  },
  sectionBody: { padding: "20px 20px 8px" },
  /* form controls */
  label: {
    fontSize: 12.5,
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: 6,
    display: "block",
    letterSpacing: 0.2,
  },
  requiredStar: { color: "#c0392b", marginLeft: 2 },
  uuidWrap: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    overflow: "hidden",
    background: "#f8fafb",
  },
  uuidInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    padding: "8px 12px",
    fontSize: 12.5,
    fontFamily: "monospace",
    color: "#6b7a8d",
    outline: "none",
  },
  uuidLabel: {
    padding: "6px 12px",
    background: "#e8f5ee",
    color: "#1a6b3c",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    borderLeft: "1px solid #d0e4d8",
  },
  /* receipt info card */
  receiptCard: {
    background: "linear-gradient(135deg, #f5fbf7 0%, #eaf5f0 100%)",
    border: "1.5px solid #c8e6d8",
    borderRadius: 10,
    padding: "16px 18px",
    marginTop: 12,
    marginBottom: 16,
  },
  receiptHeader: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#1a6b3c",
    marginBottom: 12,
  },
  receiptGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 10,
  },
  receiptItem: {
    background: "#fff",
    border: "1px solid #d8edd6",
    borderRadius: 8,
    padding: "10px 12px",
  },
  receiptItemLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#7aab8a",
    marginBottom: 3,
  },
  receiptItemValue: {
    fontSize: 13.5,
    fontWeight: 500,
    color: "#1a2332",
    wordBreak: "break-all",
  },
  /* participant types */
  typesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
  },
  typeCol: {},
  typeColHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#9aa5b4",
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: "2px solid #f0f4f8",
  },
  typeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 0",
    borderBottom: "1px solid #f8fafb",
  },
  typeLabel: {
    fontSize: 13,
    color: "#2d3748",
    fontWeight: 500,
  },
  typeInput: {
    width: 68,
    textAlign: "center",
    border: "1.5px solid #e3e8ef",
    borderRadius: 7,
    padding: "5px 8px",
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    color: "#1a2332",
    fontWeight: 600,
    transition: "border-color 0.15s",
  },
  /* buttons */
  btnRow: {
    display: "flex",
    gap: 10,
    paddingTop: 4,
    paddingBottom: 28,
    justifyContent: "flex-end",
  },
  cancelBtn: {
    padding: "10px 22px",
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    background: "#fff",
    color: "#4a5568",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
  },
  submitBtn: {
    padding: "10px 28px",
    border: "none",
    borderRadius: 8,
    background: "#1a6b3c",
    color: "#fff",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
};

/* ── Select theme override ── */
const customSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 13.5,
    borderColor: hasError ? "#c0392b" : state.isFocused ? "#1a6b3c" : "#e3e8ef",
    boxShadow: state.isFocused
      ? hasError
        ? "0 0 0 3px rgba(192,57,43,0.12)"
        : "0 0 0 3px rgba(26,107,60,0.12)"
      : "none",
    borderRadius: 8,
    fontFamily: "'DM Sans', sans-serif",
    "&:hover": { borderColor: hasError ? "#c0392b" : "#1a6b3c" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13.5,
    fontFamily: "'DM Sans', sans-serif",
    background: state.isSelected
      ? "#1a6b3c"
      : state.isFocused
      ? "#e8f5ee"
      : "#fff",
    color: state.isSelected ? "#fff" : "#1a2332",
  }),
  singleValue: (base) => ({ ...base, color: "#1a2332" }),
  placeholder: (base) => ({ ...base, color: "#9aa5b4", fontSize: 13.5 }),
  menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden" }),
  /* ✅ ensure the portal-rendered menu sits above everything */
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

/* ── Section wrapper ── */
function Section({ icon, title, children }) {
  return (
    <div style={S.sectionCard}>
      <div style={S.sectionHeader}>
        <div style={S.sectionIcon}>{icon}</div>
        <h3 style={S.sectionTitle}>{title}</h3>
      </div>
      <div style={S.sectionBody}>{children}</div>
    </div>
  );
}

/* ── Field wrapper ── */
function Field({ label, required, error, children, col = "col-md-4" }) {
  return (
    <div className={`${col} mb-3`}>
      <label style={S.label}>
        {label}
        {required && <span style={S.requiredStar}>*</span>}
      </label>
      {children}
      {error && (
        <div style={{ fontSize: 12, color: "#c0392b", marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
function ParticipantSetupForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.data;
  const type = location.state?.type || "add";
  const returnUrl = "/participantSetupList";

  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [participantTypes, setParticipantTypes] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [dropdownData, setDropdownData] = useState({ countries: [], paymentRecipt: [] });
  const [receiptLoading, setReceiptLoading] = useState(false);
  const sub_domain = localStorage.getItem('sub_domain') || "";
  const series = localStorage.getItem('series') || "";

  const [form, setForm] = useState({
    id: "",
    payment_receipt_id: "",
    uuid: "",
    payment_type:"",
    no_of_entry: "",
    name: "",
    affiliation: "",
    country: "",
    remarks: "",
    participant_type: {},
    sub_domain: sub_domain,
    series: series,
  });

  /* ── Fetches ── */
  const fetchData = useCallback(async () => {
    try { await getData(); } catch (e) { console.error(e); }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [country] = await Promise.all([getcountries()]);
      setDropdownData((prev) => ({ ...prev, countries: country.data }));
    } catch (e) { console.error(e); }
  }, []);

//   const fetchPaymentReceipt = useCallback(async () => {
//   const subDomain = localStorage.getItem("sub_domain");
//   const series = localStorage.getItem("series");

//   if (!subDomain) return;

//   try {
//     let paymentReciptData = [];

//     if (form.payment_type === "free") {
//       paymentReciptData = await getFreeParticipantData({
//         participant_type: "free",
//         sub_domain: subDomain,
//         series,
//       });
//     } 
//     else if (form.payment_type === "onsite") {
//       paymentReciptData = await getOnsiteParticipantData({
//         participant_type: "onsite",
//         sub_domain: subDomain,
//         series,
//       });
//     } 
//     else if (form.payment_type === "paid") {
//       paymentReciptData = await getPaymentReceiptData({
//         sub_domain: subDomain,
//         series,
//         event_type: "conference",
//       });
//     }

//     paymentReciptData = paymentReciptData.sort((a, b) =>
//       (a.billing_full_name || a.name || "").localeCompare(
//         b.billing_full_name || b.name || ""
//       )
//     );

//     setDropdownData((prev) => ({
//       ...prev,
//       paymentRecipt: paymentReciptData,
//     }));
//   } catch (error) {
//     console.error(error);
//   }
// }, []);

const fetchPaymentReceipt = useCallback(async (paymentType) => {
  const subDomain = localStorage.getItem("sub_domain");
  const series = localStorage.getItem("series");

  if (!subDomain) return;

  try {
    let paymentReciptData = [];

    switch (paymentType) {
      case "free":
        paymentReciptData = await getFreeParticipantData({
          participant_type: "free",
          sub_domain: subDomain,
          series,
        });
        break;

      case "onsite":
        paymentReciptData = await getOnsiteParticipantData({
          participant_type: "onsite",
          sub_domain: subDomain,
          series,
        });
        break;

      case "paid":
        paymentReciptData = await getPaymentReceiptData({
          sub_domain: subDomain,
          series,
          event_type: "conference",
        });
        break;

      default:
        paymentReciptData = [];
    }

    paymentReciptData.sort((a, b) =>
      (a.billing_full_name || a.participant_name || "")
        .localeCompare(
          b.billing_full_name || b.participant_name || ""
        )
    );

    setDropdownData((prev) => ({
      ...prev,
      paymentRecipt: paymentReciptData,
    }));
  } catch (error) {
    console.error(error);
  }
}, []);


useEffect(() => {
  if (!form.payment_type) return;

  const loadReceipts = async () => {
    setReceiptLoading(true);

    await fetchPaymentReceipt(form.payment_type);

    setReceiptLoading(false);
  };

  loadReceipts();
}, [form.payment_type, fetchPaymentReceipt]);

// useEffect(() => {
//   if (!form.payment_type) return;

//   const loadReceipts = async () => {
//     setReceiptLoading(true);

//     await fetchPaymentReceipt();

//     setReceiptLoading(false);
//   };

//   loadReceipts();
// }, [form.payment_type]);

  const fetchParticipantTypes = useCallback(async () => {
    try {
      const res = await getParticipantTypeData();
      setParticipantTypes(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchData(), fetchDropdownData(), fetchParticipantTypes()]);
      setLoading(false);
    };
    init();
  }, [fetchData, fetchDropdownData,fetchParticipantTypes]);

  useEffect(() => {
    if (type === "add") setForm((p) => ({ ...p, uuid: nanoid() }));
  }, [type]);

  useEffect(() => {
    if (type === "edit" && editData) {
      setForm({
        id: editData.id,
        payment_receipt_id: editData.payment_receipt_id || "",
        uuid: editData.uuid || "",
        payment_type: editData.payment_type || "",
        no_of_entry: editData.no_of_entry || "",
        name: editData.name || "",
        affiliation: editData.affiliation || "",
        country: editData?.country || "",
        remarks: editData.remarks || "",
        participant_type: editData.participant_type || {},
      });
    }
  }, [type, editData]);

  useEffect(() => {
    if ( dropdownData?.paymentRecipt?.length) {
      const receipt = dropdownData.paymentRecipt.find(
        (item) => item.payment_id === form.payment_receipt_id
      );
      setSelectedReceipt(receipt || null);
    }
  }, [type, form.payment_receipt_id, dropdownData]);

  /* ── Handlers ── */
  const handleChange = (e) => {
  const { name, value } = e.target;

  setForm((p) => ({
    ...p,
    [name]: value,
    ...(name === "payment_type" && {
      payment_receipt_id: "",
    }),
  }));

  if (name === "payment_type") {
    setSelectedReceipt(null);

    setDropdownData((prev) => ({
      ...prev,
      paymentRecipt: [],
    }));
  }

  setErrors((p) => ({
    ...p,
    [name]: "",
  }));
};


  const handleNumberChange = (uuid, category_code, value) => {
    let val = parseInt(value);
    if (val < 1) val = 1;
    if (val > 3) val = 3;
    setForm((p) => ({
      ...p,
      participant_type: {
        ...p.participant_type,
        [uuid]: { ...p.participant_type?.[uuid], [category_code]: val },
      },
    }));
  };

  const validateForm = () => {
    const e = {};
    if (!form.name) e.name = "Name is required";
    if (!form.affiliation) e.affiliation = "Affiliation is required";
    if (!form.country) e.country = "Country is required";
    if (!form.uuid) e.uuid = "UUID is required";
    if (!form.payment_type) e.payment_type = "Payment Type is required";
    if (!form.no_of_entry) e.no_of_entry = "No of Entry is required";
    if (!form.payment_receipt_id) e.payment_receipt_id = "Payment Receipt is required";
    if (type === "edit") {
      if (!form.remarks)
        e.remarks = "Remarks is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      if (type === "add") await addData(form);
      else await updateData(form);
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Participant ${type === "add" ? "added" : "updated"} successfully`,
        timer: 2000,
        showConfirmButton: false,
      });
      navigate(returnUrl);
    } catch(error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Error", text: "Something went wrong" });
    } finally {
      setBtnLoading(false);
    }
  };

  /* ── Derived data ── */
  const column1 = participantTypes.filter((i) => i.reference_uuid === "1");
  const column2 = participantTypes.filter((i) => i.reference_uuid === "2");
  const column3 = participantTypes.filter((i) => i.reference_uuid === "3");


  const paymentReceiptOptions = dropdownData.paymentRecipt.map((item) => {
    let value = "";
    let label = "";

    switch (form.payment_type) {
      case "paid":
        value = item.payment_id;
        label = `${item.billing_full_name} (${item.payment_id})`;
        break;

      case "free":
        value = item.payment_id;
        label = `${item.billing_full_name} (${item.payment_id})`;
        break;

      case "onsite":
        value = item.payment_id;
        label = `${item.billing_full_name} (${item.payment_id})`;
        break;

      default:
        break;
    }

    return { value, label };
  });

  const countriesOptions = dropdownData?.countries?.map((item) => ({
    value: item.code,
    label: item.name,
  }));

  const colLabels = ["Group A", "Group B", "Group C"];

  /* ── Render ── */
  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100" style={S.page}>
        <Header />
        {loading ? (
          <FormSkeleton />
        ) : (
          <div style={S.container}>
            {/* ── Top bar ── */}
            <div style={S.topBar}>
              <div>
                <div style={S.breadcrumb}>Conference Program &rsaquo; Participant Setup</div>
                <h2 style={S.pageTitle}>
                  {type === "add" ? "Add Participant" : "Edit Participant"}
                </h2>
              </div>
              <button
                style={S.backBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#1a6b3c";
                  e.currentTarget.style.color = "#1a6b3c";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e3e8ef";
                  e.currentTarget.style.color = "#4a5568";
                }}
                onClick={() => navigate(returnUrl)}
              >
                ← Back to List
              </button>
            </div>

            {/* ── Section 1: Personal Info ── */}
            <Section icon="👤" title="Personal Information">
              <div className="row">
                <Field label="Full Name" required error={errors.name}>
                  <Form.Control
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.name ? "#c0392b" : "#e3e8ef" }}
                    placeholder="Enter Full Name"
                  />
                </Field>

                <Field label="Affiliation / Institution" required error={errors.affiliation}>
                  <Form.Control
                    type="text"
                    name="affiliation"
                    value={form.affiliation}
                    onChange={handleChange}
                    isInvalid={!!errors.affiliation}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.affiliation ? "#c0392b" : "#e3e8ef" }}
                    placeholder="University / Hospital"
                  />
                </Field>

                {/* ✅ menuPortalTarget + menuPosition fix for Country select */}
                <Field label="Country" required error={errors.country}>
                  <Select
                    name="country"
                    value={countriesOptions?.find((o) => o.value === form.country?.trim())}
                    placeholder="Select a country"
                    onChange={(opt) =>
                      handleChange({ target: { name: "country", value: opt?.value || "" } })
                    }
                    options={countriesOptions}
                    styles={customSelectStyles(!!errors.country)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </Field>

                {/* UUID */}
                <Field label="UUID" required error={errors.uuid} col="col-md-4">
                  <div style={S.uuidWrap}>
                    <input
                      style={S.uuidInput}
                      value={form.uuid}
                      readOnly
                    />
                    <span style={S.uuidLabel}>AUTO</span>
                  </div>
                </Field>

                <Field label="RegistrationType" required error={errors.payment_type} col="col-md-4">
                  <Form.Select
                    name="payment_type"
                    value={form.payment_type}
                    onChange={handleChange}
                    isInvalid={!!errors.payment_type}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.payment_type ? "#c0392b" : "#e3e8ef" }}
                  >
                    <option value="">Select</option>
                   {[
                  { value: "free", label: "Free Registration" },
                  { value: "paid", label: "Paid Registration" },
                  { value: "onsite", label: "Onsite Registration" },
                ].map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                  </Form.Select>
                </Field>

                <Field label="No of Entry" required error={errors.no_of_entry} col="col-md-4">
                  <Form.Select
                    name="no_of_entry"
                    value={form.no_of_entry}
                    onChange={handleChange}
                    isInvalid={!!errors.no_of_entry}
                    style={{ fontSize: 13.5, borderRadius: 8, borderColor: errors.no_of_entry ? "#c0392b" : "#e3e8ef" }}
                  >
                    <option value="">Select</option>
                    {[1, 2].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Form.Select>
                </Field>
              </div>
            </Section>

            {/* ── Section 2: Participant Types ── */}
            <Section icon="🏷️" title="Participant Types">
              <div style={S.typesGrid}>
                {[column1, column2, column3].map((col, ci) => (
                  <div key={ci} style={S.typeCol}>
                    <div style={S.typeColHeader}>{colLabels[ci]}</div>
                    {col.length === 0 ? (
                      <div style={{ fontSize: 12.5, color: "#c8d0db", fontStyle: "italic", paddingTop: 8 }}>
                        No types in this group
                      </div>
                    ) : (
                      col.map((item) => (
                        <div key={item.id} style={S.typeRow}>
                          <label style={S.typeLabel}>{item.category_name}</label>
                          <input
                            type="number"
                            min={1}
                            max={3}
                            style={S.typeInput}
                            value={
                              form?.participant_type[item.reference_uuid]?.[item.category_code] || ""
                            }
                            onFocus={(e) => (e.target.style.borderColor = "#1a6b3c")}
                            onBlur={(e) => (e.target.style.borderColor = "#e3e8ef")}
                            onChange={(e) =>
                              handleNumberChange(item.reference_uuid, item.category_code, e.target.value)
                            }
                            placeholder="—"
                          />
                        </div>
                      ))
                    )}
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Section 3: Payment ── */}
            <Section icon="💳" title={`${form.payment_type.charAt(0).toUpperCase() + form.payment_type.slice(1)} Registration Receipt`}>
              <div className="row">
                <Field
                  label="Registration Receipt"
                  required
                  error={errors.payment_receipt_id}
                  col="col-md-6"
                >
                  {receiptLoading ? (
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                      <span>Loading receipts...</span>
                    </div>
                  ) : (
                  <Select
                    name="payment_receipt_id"
                    value={paymentReceiptOptions?.find(
                      (o) => o.value === form.payment_receipt_id?.trim()
                    )}
                    placeholder="Select receipt ID"
                    onChange={(opt) => {
                    const value = opt?.value || "";

                    handleChange({
                      target: {
                        name: "payment_receipt_id",
                        value,
                      },
                    });

                    const receipt = dropdownData.paymentRecipt.find(
                      (item) =>
                        (form.payment_type === "paid"
                          ? item.payment_id
                          : item.participant_id) === value
                    );

                    setSelectedReceipt(receipt || null);
                  }}
                    options={paymentReceiptOptions}
                    styles={customSelectStyles(!!errors.payment_receipt_id)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
                </Field>
              </div>

              {/* Receipt detail card */}
              {selectedReceipt && (
                <div style={S.receiptCard}>
                  <div style={S.receiptHeader}>Receipt Details</div>
                  <div style={S.receiptGrid}>
                    {[
                      ["Payment ID",   selectedReceipt.payment_id],
                      ["Full Name",    selectedReceipt.billing_full_name],
                      ["Email",        selectedReceipt.billing_email],
                      ["Reg Category", selectedReceipt.reg_category],
                      ["Date Of Payment", selectedReceipt.date_of_payment],
                      ["No of Ticket", selectedReceipt.no_of_ticket],
                    ].map(([lbl, val]) => (
                      <div key={lbl} style={S.receiptItem}>
                        <div style={S.receiptItemLabel}>{lbl}</div>
                        <div style={S.receiptItemValue}>{val || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* ── Section 4: Remarks ── */}
            <Section icon="📝" title="Additional Notes">
              <div className="row">
                <Field label="Remarks" col="col-md-12"  required={type === "edit"}  error={errors.remarks}>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="remarks"
                    value={form.remarks}
                    onChange={handleChange}
                    placeholder="Any additional notes or remarks"
                    style={{
                      fontSize: 13.5,
                      borderRadius: 8,
                      borderColor: errors.remarks ? "#c0392b" : "#e3e8ef",
                      resize: "vertical",
                      lineHeight: 1.6,
                    }}
                    styles={customSelectStyles(!!errors.remarks)}
                  />
                </Field>
              </div>
            </Section>

            {/* ── Submit row ── */}
            <div style={S.btnRow}>
              <button
                type="button"
                style={S.cancelBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#c0392b";
                  e.currentTarget.style.color = "#c0392b";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e3e8ef";
                  e.currentTarget.style.color = "#4a5568";
                }}
                onClick={() => navigate(returnUrl)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  ...S.submitBtn,
                  opacity: btnLoading ? 0.75 : 1,
                  cursor: btnLoading ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!btnLoading) {
                    e.currentTarget.style.background = "#2d8a55";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(26,107,60,0.28)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a6b3c";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
                onClick={handleSubmit}
                disabled={btnLoading}
              >
                {btnLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    {type === "add" ? "Adding…" : "Updating…"}
                  </>
                ) : (
                  <>{type === "add" ? "✓ Add Participant" : "✓ Update Participant"}</>
                )}
              </button>
            </div>
          </div>
        )}
        <Footer />
      </div>
    </div>
  );
}

export default ParticipantSetupForm;